import { useState } from "react";
import { gtagReportConversion } from "@/lib/gtag";
import { SEOHead } from "@/components/SEOHead";
import { glampingProductSchema, aggregateRatingFor } from "@/lib/schema";
import { Header } from "@/components/layout/Header";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getPostsBySlugs } from "@/content/blogPosts";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { WhatsNearby } from "@/components/seo/WhatsNearby";
import { Testimonials } from "@/components/seo/Testimonials";
import { seoCopy } from "@/content/seoCopy";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { GlampingBookingPopupModal } from "@/components/home/GlampingBookingPopupModal";
import { 
  Users, 
  Clock, 
  Waves, 
  Bed,
  CheckCircle, 
  Sun,
  Moon,
  Snowflake,
  Zap,
  Coffee,
  Utensils,
  TreePine,
  Flame,
  Car,
  ShowerHead,
  Wifi,
  Music,
  Dog,
  Info,
  Sparkles,
  Star
} from "lucide-react";

// Images
import glampingHero from "@/assets/glamping-hero.jpg";
import glampingTentExterior from "@/assets/gallery/glamping-tent-exterior.jpg";
import glampingLights from "@/assets/gallery/glamping-lights.jpg";
import campfireSmores from "@/assets/gallery/campfire-smores.jpg";
import aerialCabanas from "@/assets/gallery/aerial-cabanas.jpg";
import riverView from "@/assets/gallery/river-view.jpg";
import tikiLounge from "@/assets/gallery/tiki-lounge.jpg";
import treeCanopyPath from "@/assets/gallery/tree-canopy-path.jpg";
import clearRiver from "@/assets/gallery/clear-river.jpg";

import tikiLoungeTents from "@/assets/glamping/tiki-lounge-tents.jpg";
import tentsSunset from "@/assets/glamping/tents-sunset.jpg";
import tentBeds from "@/assets/glamping/tent-beds.jpg";
import tentCabanaDaytime from "@/assets/glamping/tent-cabana-daytime.webp";
import tentAcUnit from "@/assets/glamping/tent-ac-unit.jpg";
import firepitRiver from "@/assets/glamping/firepit-river.jpg";
import stepsFromRiver from "@/assets/glamping/steps-from-river.jpg";
import starlitNights from "@/assets/glamping/starlit-nights.jpg";
import fullElectricity from "@/assets/glamping/full-electricity.jpg";
import tentCabanaSetup from "@/assets/glamping/tent-cabana-setup.jpg";
const glampingFeatures = [
  {
    icon: Snowflake,
    title: "Air Conditioned",
    description: "Stay cool and comfortable with AC units in every tent",
    image: tentAcUnit
  },
  {
    icon: Bed,
    title: "Queen Foam Mattresses",
    description: "Comfortable mattresses for a restful sleep",
    image: tentBeds
  },
  {
    icon: Zap,
    title: "Full Electricity",
    description: "Charge your devices, run fans, and enjoy modern conveniences",
    image: fullElectricity
  },
  {
    icon: Waves,
    title: "Steps from the River",
    description: "Wake up and walk straight to the crystal-clear Guadalupe",
    image: stepsFromRiver
  },
  {
    icon: Moon,
    title: "Starlit Nights",
    description: "Fall asleep to the sounds of nature under the Texas sky",
    image: starlitNights
  },
  {
    icon: Flame,
    title: "BBQ & Fire Pit",
    description: "Gather around a campfire for s'mores and stories",
    image: firepitRiver
  }
];

const tentTypes = [
  {
    title: "Cozy Tent",
    capacity: "2 Guests",
    price: "$99",
    description: "Perfect for couples seeking a romantic riverside escape",
    features: ["Queen bed", "AC & heating", "Private deck", "River views", "Fire pit access"],
    popular: false
  },
  {
    title: "Family Tent",
    capacity: "4 Guests",
    price: "$149",
    description: "Spacious tent with room for the whole family",
    features: ["Queen bed + bunks", "AC & heating", "Large deck", "River views", "Private fire pit", "Extra seating"],
    popular: true
  },
  {
    title: "Deluxe Tent",
    capacity: "6 Guests",
    price: "$199",
    description: "Our largest tent with premium amenities",
    features: ["King bed + queen", "AC & heating", "Wraparound deck", "Best river views", "Private fire pit", "Outdoor dining", "Mini fridge"],
    popular: false
  }
];

const includedAmenities = [
  { icon: Bed, text: "Comfortable foam Mattresses" },
  { icon: Snowflake, text: "Air conditioning & heating" },
  { icon: Zap, text: "Electricity & lighting" },
  { icon: ShowerHead, text: "Clean bathhouse nearby" },
  { icon: Car, text: "Dedicated parking spot" },
  { icon: Wifi, text: "Free Wi-Fi on property" },
  { icon: Flame, text: "Fire pit with seating" },
  { icon: Waves, text: "Private river access" }
];

const whatToBring = [
  { icon: Utensils, text: "Food & drinks (no kitchen)" },
  { icon: Coffee, text: "Coffee & snacks" },
  { icon: TreePine, text: "Outdoor games & activities" },
  { icon: Music, text: "Bluetooth speaker" }
];

const rules = [
  { icon: Moon, text: "Quiet hours 10 PM - 8 AM", allowed: true },
  { icon: Dog, text: "No pets allowed", allowed: false },
  { icon: Flame, text: "Fires in designated pits only", allowed: true },
  { icon: Sparkles, text: "No glass containers", allowed: false },
  { icon: Info, text: "Check-in 3 PM / Check-out 11 AM", allowed: true }
];

const galleryImages = [
  { src: tentBeds, alt: "Inside glamping tent with cots and AC unit" },
  { src: tikiLoungeTents, alt: "Glamping tents with tiki lounge seating area" },
  { src: tentCabanaDaytime, alt: "Bell tent with tiki cabana and fire pit during the day" },
  { src: tentsSunset, alt: "Glamping tents at sunset with tiki huts and string lights" },
  { src: glampingTentExterior, alt: "Glamping tent exterior at sunset" },
  { src: glampingLights, alt: "Glamping area with string lights" },
  { src: campfireSmores, alt: "Making s'mores at the campfire" },
  { src: riverView, alt: "Beautiful river view from glamping area" },
  { src: tikiLounge, alt: "Relaxing tiki lounge area" },
  { src: treeCanopyPath, alt: "Shaded path through the property" },
  { src: clearRiver, alt: "Crystal clear Guadalupe River" },
  { src: aerialCabanas, alt: "Aerial view of the property" }
];

const faqs = [
  {
    question: "What's the difference between glamping and regular camping?",
    answer: "Glamping combines the outdoor experience with comfortable amenities. Our tents feature real beds with quality linens, air conditioning, electricity, and more — no sleeping bags or roughing it required!"
  },
  {
    question: "What time is check-in and check-out?",
    answer: "Check-in is at 3 PM and check-out is at 11 AM. Early check-in or late check-out may be available upon request, subject to availability."
  },
  {
    question: "Are bathrooms inside the tents?",
    answer: "Tents do not have private bathrooms. We have clean, well-maintained bathhouses located a short walk from all glamping sites with showers, restrooms, and sinks."
  },
  {
    question: "Can I cook in the tent?",
    answer: "Cooking inside tents is not permitted for safety reasons. However, you can bring a portable grill for outdoor cooking, and we have charcoal grills available. Many guests bring coolers with prepared foods and snacks."
  },
  {
    question: "Is glamping available year-round?",
    answer: "Yes! Our tents are equipped with both AC and heating, making them comfortable in Texas summers and mild winters. The river is beautiful in every season."
  },
  {
    question: "Can I add tubing to my glamping stay?",
    answer: "Absolutely! Tubing is available for $24.99 per person and is one of the most popular add-ons for glamping guests. Float the river during the day and relax by the fire at night."
  },
  {
    question: "Are pets allowed?",
    answer: "No, we love pets but unfortunately our insurance does not allow them on the property."
  },
  {
    question: "What happens if it rains?",
    answer: "Our tents are waterproof and sturdy — light rain won't dampen your experience! For severe weather, we'll work with you on rescheduling. Check our cancellation policy for details."
  }
];

const Glamping = () => {
  const [glampingOpen, setGlampingOpen] = useState(false);
  
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Glamping Near New Braunfels on the Guadalupe | Son's"
        description="Glamping near New Braunfels in riverside bell tents with AC & real beds. Plus tent camping on River Road. Family getaways start at $99."
        canonical="/glamping"
        ogImage="/og/glamping.jpg"
        schemaJson={[
          {
            ...glampingProductSchema(
              "https://sonsguadalupe.com/glamping",
              "https://sonsguadalupe.com/og/glamping.jpg",
            ),
            aggregateRating: aggregateRatingFor("glamping"),
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
              { "@type": "ListItem", position: 2, name: "Glamping", item: "https://sonsguadalupe.com/glamping" },
            ],
          },
        ]}
      />
      <GlampingBookingPopupModal open={glampingOpen} onOpenChange={setGlampingOpen} />
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img fetchPriority="high" loading="eager" decoding="async"
            src={glampingHero}
            alt="Luxury glamping tent on the Guadalupe River"
            className="w-full h-full object-cover"
            
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/70 via-charcoal/50 to-transparent" />
        </div>
        
        <div className="relative z-10 container-narrow mx-auto px-4 md:px-8 pt-32 pb-16">
          <div className="max-w-2xl">
            <p className="text-coral-orange font-medium mb-4 text-lg">Overnight Glamping</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Sleep Under the Stars, Wake Up by the River
            </h1>
            <p className="text-white/90 text-lg md:text-xl mb-8">
              AC tents with real beds, fire pits, and the sounds of the Guadalupe lulling you to sleep. All the magic of camping, none of the hassle.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button variant="hero" size="xl" onClick={() => { gtagReportConversion(); setGlampingOpen(true); }}>
                Book Glamping — Starting at $99
              </Button>
              <Link to="/book-now">
                <Button variant="hero-outline" size="xl">
                  View Availability
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section-padding bg-cream">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Camping Comfort,
            </h2>
            <p className="text-charcoal/70 text-lg max-w-2xl mx-auto">
              Experience the outdoors in comfort. Get a good nights rest & be ready for a fun-filled day
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {glampingFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-shadow"
              >
                {feature.image ? (
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={feature.image} 
                      alt={feature.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="p-6 pb-0">
                    <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-teal" />
                    </div>
                  </div>
                )}
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold text-charcoal mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-charcoal/70">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="section-padding bg-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Your Riverside Retreat
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div 
                key={index}
                className={`relative overflow-hidden rounded-xl ${
                  index === 0 || index === 7 ? 'col-span-2 row-span-2' : ''
                }`}
              >
                <img
                  src={image.src}
                  loading="lazy"
                  alt={image.alt}
                  className="w-full h-full object-cover aspect-square hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included in Your Glamping Setup */}
      <section className="section-padding bg-teal text-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Starting at Only $99 a Night
              </h2>
              <p className="text-white/90 text-xl">
                We do all the setup while you have all the fun!
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-elevated">
              <img 
                src={tentCabanaSetup} 
                alt="Glamping tent with tiki cabana setup"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Tent Setup Includes */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="font-heading text-2xl font-bold mb-6 flex items-center gap-3">
                <Bed className="w-7 h-7 text-coral-orange" />
                Your Glamping Tent Setup Includes
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-coral-orange flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">Overnight entry for 5 people</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-coral-orange flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">1 car parking spot</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-coral-orange flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">16' bell tent</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-coral-orange flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">Stand up air-conditioner</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-coral-orange flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">Two high powered fans</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-coral-orange flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">Two queen sized beds + one twin sized bed</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-coral-orange flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">Extension cord, bedside table & lantern</span>
                </li>
              </ul>
            </div>

            {/* Cabana Access */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="font-heading text-2xl font-bold mb-6 flex items-center gap-3">
                <TreePine className="w-7 h-7 text-coral-orange" />
                Cabana Access Included
              </h3>
              <p className="text-white/80 mb-4">
                Your Glamping Tent setup also includes evening, overnight and morning access to a cabana which has:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-coral-orange flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">Charcoal BBQ pit</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-coral-orange flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">Picnic table</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-coral-orange flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">Wicker furniture</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-coral-orange flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">Electrical outlets</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-coral-orange flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">Running water and light at your site</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-white/70 text-sm italic">
              *Note: Sheets, blankets and pillows are not provided — you are responsible for bringing these.
            </p>
            <Button variant="hero" size="xl" className="mt-6" onClick={() => setGlampingOpen(true)}>
              Book Your Glamping Experience
            </Button>
          </div>
        </div>
      </section>

      {/* What's Included + What to Bring */}
      <section className="section-padding bg-cream">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* What's Included */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-charcoal mb-6">
                What's Included
              </h2>
              <div className="bg-white rounded-2xl p-6 shadow-soft">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {includedAmenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <amenity.icon className="w-5 h-5 text-teal" />
                      </div>
                      <span className="text-charcoal/80">{amenity.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* What to Bring */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-charcoal mb-6">
                What to Bring
              </h2>
              <div className="bg-white rounded-2xl p-6 shadow-soft">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {whatToBring.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-coral-orange/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-coral-orange" />
                      </div>
                      <span className="text-charcoal/80">{item.text}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-charcoal/60 text-sm">
                  Pro tip: Bring a cooler with your favorite foods and drinks. There's no kitchen in the tents, but you have access to grills and plenty of outdoor space for dining.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Day + Night Experience */}
      <section className="section-padding bg-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img loading="lazy" decoding="async"
                src={glampingLights}
                alt="Glamping area at night with string lights"
                className="rounded-2xl shadow-elevated"
              />
            </div>
            
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-6">
                The Complete Experience
              </h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-coral-orange/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Sun className="w-6 h-6 text-coral-orange" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-charcoal mb-1">During the Day</h3>
                    <p className="text-charcoal/70">Float the river, relax in your cabana, swim in crystal-clear water, and soak up the Texas sun.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-deep-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Moon className="w-6 h-6 text-deep-green" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-charcoal mb-1">At Night</h3>
                    <p className="text-charcoal/70">Gather around your fire pit, roast s'mores, stargaze, and fall asleep to the sounds of the river.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Coffee className="w-6 h-6 text-teal" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-charcoal mb-1">In the Morning</h3>
                    <p className="text-charcoal/70">Wake up refreshed, enjoy coffee on your deck, and take a peaceful morning dip before checkout.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Glamping vs Cabin vs Tent Camping comparison */}
      <section className="section-padding bg-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-10 max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Glamping vs. Cabana vs. Tent Camping — Which Is Right for You?
            </h2>
            <p className="text-charcoal/70 text-lg">
              All three are on the Guadalupe River at Son's. Here's how the experiences compare side-by-side.
            </p>
          </div>

          <div className="overflow-x-auto bg-cream/40 rounded-2xl shadow-soft">
            <table className="w-full text-sm md:text-base">
              <thead>
                <tr className="bg-teal text-white">
                  <th className="text-left p-4 font-heading min-w-[140px]">Feature</th>
                  <th className="text-left p-4 font-heading min-w-[180px] bg-coral-orange">Glamping Bell Tent</th>
                  <th className="text-left p-4 font-heading min-w-[180px]">Day Cabana</th>
                  <th className="text-left p-4 font-heading min-w-[180px]">Tent Camping</th>
                </tr>
              </thead>
              <tbody className="text-charcoal/80">
                {[
                  { f: "Stay type", g: "Overnight", c: "Day-use 10:30 AM – 6:30 PM", t: "Overnight (bring your own tent)" },
                  { f: "Sleeps", g: "5 guests (2 queens + twin)", c: "Up to 12 day-guests", t: "Up to 6 per site" },
                  { f: "Air conditioning", g: "Stand-up AC + 2 fans", c: "Open-air w/ ceiling fans", t: "None — bring fans" },
                  { f: "Real beds", g: "Queen foam mattresses + twin", c: "N/A (day-use)", t: "Bring your own" },
                  { f: "Electricity", g: "Full electric, lantern, outlets", c: "Outlets at cabana", t: "Site outlet only" },
                  { f: "Private fire pit", g: "Yes", c: "Charcoal BBQ only", t: "Yes" },
                  { f: "River access", g: "Steps from the Guadalupe", c: "Directly on river", t: "Riverside sites" },
                  { f: "Starting price", g: "From $99/night", c: "From $69/day", t: "From ~$45/site" },
                  { f: "Best for", g: "Comfort-first overnight stays", c: "Day trips with the family", t: "Traditional campers" },
                ].map((row) => (
                  <tr key={row.f} className="border-t border-cream">
                    <td className="p-4 font-semibold text-charcoal">{row.f}</td>
                    <td className="p-4 bg-cream/40">{row.g}</td>
                    <td className="p-4">{row.c}</td>
                    <td className="p-4">{row.t}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/cabanas"><Button variant="outline" size="lg">See Cabanas</Button></Link>
            <Link to="/tent-camping-new-braunfels"><Button variant="outline" size="lg">See Tent Camping</Button></Link>
            <Link to="/luxury-glamping-new-braunfels"><Button variant="outline" size="lg">Compare Glamping Operators</Button></Link>
          </div>
        </div>
      </section>

      {/* Rules & Policies */}
      <section className="section-padding bg-cream">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
                Good to Know
              </h2>
              <p className="text-charcoal/70 text-lg">
                A few guidelines to ensure everyone has a peaceful stay.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-soft">
              <div className="space-y-4">
                {rules.map((rule, index) => (
                  <div key={index} className="flex items-center gap-4 py-3 border-b border-border last:border-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      rule.allowed ? 'bg-deep-green/10' : 'bg-coral-orange/10'
                    }`}>
                      {rule.allowed ? (
                        <CheckCircle className="w-5 h-5 text-deep-green" />
                      ) : (
                        <Info className="w-5 h-5 text-coral-orange" />
                      )}
                    </div>
                    <span className="text-charcoal">{rule.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Glamping FAQs
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <details 
                key={index}
                className="group bg-cream rounded-xl p-6 cursor-pointer"
              >
                <summary className="flex items-center justify-between font-heading font-bold text-charcoal text-lg list-none">
                  {faq.question}
                  <Sun className="w-5 h-5 text-teal group-open:rotate-45 transition-transform" />
                </summary>
                <p className="mt-4 text-charcoal/70 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-deep-green text-white">
        <div className="container-narrow mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Ready for Your Riverside Glamping Escape?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Book your tent and experience the Guadalupe like never before — comfort, nature, and unforgettable memories.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="hero" size="xl" onClick={() => setGlampingOpen(true)}>
              Book Your Glamping Stay
            </Button>
            <Button variant="hero-outline" size="xl" className="border-white text-white hover:bg-white/10">
              Call 877-577-7667
            </Button>
          </div>
        </div>
      </section>

      <WhatsNearby />

      <Testimonials category="glamping" />
      <SEOContentBlock {...seoCopy.glamping} />
      <RelatedReads posts={getPostsBySlugs(["labor-day-weekend-guadalupe-river", "river-road-new-braunfels-guide", "best-time-to-tube-guadalupe-river"])} heading="From our blog" />
      <Footer />
    </div>
  );
};

export default Glamping;
